// This has to be a mjs file to be able to import it in next.config.mjs

import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

/**
 * Used to access environment variables while providing server-client separation.
 * Client env-s are prefixed with `NEXT_PUBLIC_` and are available in the browser.
 *
 * Server env-s are not prefixed and are only available on the server.
 * Using server env-s in the browser will result in a runtime error.
 *
 * @example
 * import { env } from '@env'; // @env is an alias to this file, don't use absolute paths
 *
 *  function MyComponent() {
 *    return <div>{ env.NEXT_PUBLIC_API_URL }</div>;
 *  }
 */
export const env = createEnv({
	server: {
		API_KEY: z.string().min(1),
		ANALYZE: z
			.string()
			.default('false')
			.refine((s) => s === 'true' || s === 'false')
			.transform((s) => s === 'true'),
		REVALIDATE_TOKEN: z.string().min(1).default('REVALIDACIJA')
	},
	client: {
		// This is mapped from the server env to the client env. We still use NODE_ENV to determine the environment.
		NEXT_PUBLIC_NODE_ENV: z.string().min(1).default('development'),
		NEXT_PUBLIC_APP_ENV: z.string().min(1).default('dev'),
		NEXT_PUBLIC_API_URL: z.string().min(1).default('https://api.pickleballdev.net/v1'),
		NEXT_PUBLIC_API_URL_V2: z.string().min(1).default('https://api.pickleballdev.net/v2'),
		NEXT_PUBLIC_MANAGE_V1: z.string().min(1).default('https://manage.pickleballdev.net'),
		NEXT_PUBLIC_MANAGE_V2: z.string().min(1).default('https://managev2.pickleballdev.net'),
		NEXT_PUBLIC_HOME_URL: z.string().min(1).default('http://localhost:3000'),
		NEXT_PUBLIC_HOME_ROOT_URL: z.string().min(1).default('pickleballdev.net'),
		NEXT_PUBLIC_SSO_ENDPOINT: z.string().min(1).default('http://localhost:3000/login'),
		NEXT_PUBLIC_IMAGE_CDN: z.string().min(1).default('https://images.pickleball.com'),
		NEXT_PUBLIC_REWRITE_DOMAIN_NAME: z.string().min(1).default('pickleballdev.net'),
		NEXT_PUBLIC_MAIN_CHECKOUT_URL: z.string().min(1).default('https://checkout.pickleballdev.net'),
		NEXT_PUBLIC_PT_URL: z.string().min(1).default('https://pickleballtournamentsdev.net'),
		NEXT_PUBLIC_PC_URL: z.string().min(1).default('https://pickleballclubsdev.net'),
		NEXT_PUBLIC_PL_URL: z.string().min(1).default('https://pickleballleaguesdev.net'),
		NEXT_PUBLIC_PTL_URL: z.string().min(1).default('https://pickleballteamleaguesdev.net'),
		NEXT_PUBLIC_PTLC_URL: z.string().min(1).default('https://checkout.pickleballteamleaguesdev.net'),
		NEXT_PUBLIC_TEAM_LEAGUES_CHECKOUT: z.string().min(1).default('https://checkout.pickleballteamleaguesdev.net'),
		NEXT_PUBLIC_RTE_ENABLED: z.string().min(1).default('true'),
		NEXT_PUBLIC_TLBRACKETS_URL: z.string().min(1).default('https://brackets.pickleballteamleaguesdev.net')
	},
	experimental__runtimeEnv: {
		// This is mapped from the server env to the client env.
		NEXT_PUBLIC_NODE_ENV: process.env.NODE_ENV,
		// Public
		NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
		NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
		NEXT_PUBLIC_API_URL_V2: process.env.NEXT_PUBLIC_API_URL_V2,
		NEXT_PUBLIC_MANAGE_V1: process.env.NEXT_PUBLIC_MANAGE_V1,
		NEXT_PUBLIC_MANAGE_V2: process.env.NEXT_PUBLIC_MANAGE_V2,
		NEXT_PUBLIC_HOME_URL: process.env.NEXT_PUBLIC_HOME_URL,
		NEXT_PUBLIC_HOME_ROOT_URL: process.env.NEXT_PUBLIC_HOME_ROOT_URL,
		NEXT_PUBLIC_SSO_ENDPOINT: process.env.NEXT_PUBLIC_SSO_ENDPOINT,
		NEXT_PUBLIC_IMAGE_CDN: process.env.NEXT_PUBLIC_IMAGE_CDN,
		NEXT_PUBLIC_REWRITE_DOMAIN_NAME: process.env.NEXT_PUBLIC_REWRITE_DOMAIN_NAME,
		NEXT_PUBLIC_MAIN_CHECKOUT_URL: process.env.NEXT_PUBLIC_MAIN_CHECKOUT_URL,
		NEXT_PUBLIC_PT_URL: process.env.NEXT_PUBLIC_PT_URL,
		NEXT_PUBLIC_PC_URL: process.env.NEXT_PUBLIC_PC_URL,
		NEXT_PUBLIC_PL_URL: process.env.NEXT_PUBLIC_PL_URL,
		NEXT_PUBLIC_PTL_URL: process.env.NEXT_PUBLIC_PTL_URL,
		NEXT_PUBLIC_PTLC_URL: process.env.NEXT_PUBLIC_PTLC_URL,
		NEXT_PUBLIC_TEAM_LEAGUES_CHECKOUT: process.env.NEXT_PUBLIC_TEAM_LEAGUES_CHECKOUT,
		NEXT_PUBLIC_RTE_ENABLED: process.env.NEXT_PUBLIC_RTE_ENABLED,
		NEXT_PUBLIC_TLBRACKETS_URL: process.env.NEXT_PUBLIC_TLBRACKETS_URL
	},
	skipValidation: process.env.SKIP_CI_VALIDATION_ENV === '1' // Skip validation for Github Actions
});
